import {
  GRID_GAP,
  GRID_H_PADDING,
  GRID_PADDING_TOP,
  SPOT_BASE_SIZE
} from './RoomLayoutGrid.styles';
import {
  SPOT_GRID_DEFAULT_ZOOM,
  SPOT_GRID_MAX_ZOOM,
  SPOT_GRID_MIN_ZOOM
} from './spotGridZoomConstants';

export function clampZoom(scale: number, minimumScale: number = SPOT_GRID_DEFAULT_ZOOM): number {
  'worklet';
  const supportedMinimum = Math.min(
    SPOT_GRID_DEFAULT_ZOOM,
    Math.max(SPOT_GRID_MIN_ZOOM, minimumScale)
  );
  return Math.min(SPOT_GRID_MAX_ZOOM, Math.max(supportedMinimum, scale));
}

function clampAxis(translate: number, viewportSize: number, scaledSize: number): number {
  'worklet';
  if (scaledSize <= viewportSize) {
    const min = 0;
    const max = viewportSize - scaledSize;
    return Math.min(max, Math.max(min, translate));
  }
  const min = viewportSize - scaledSize;
  const max = 0;
  return Math.min(max, Math.max(min, translate));
}

function getAxisDefaultTranslation(
  viewportSize: number,
  scaledSize: number,
  alignToStartWhenOverflow: boolean,
  alignToStartWhenFits: boolean
): number {
  'worklet';
  if (scaledSize <= viewportSize) {
    return alignToStartWhenFits ? 0 : (viewportSize - scaledSize) / 2;
  }
  if (alignToStartWhenOverflow) {
    return 0;
  }
  return (viewportSize - scaledSize) / 2;
}

/**
 * Default pan offset when the grid loads or resets.
 * - Horizontal: centered when wider than the viewport (legacy ScrollView behavior).
 * - Vertical: top-aligned when taller than the viewport so the first row is fully visible.
 */
export function getDefaultPanTranslation(
  scale: number,
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  'worklet';
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return { x: 0, y: 0 };
  }

  let x = 0;
  let y = 0;
  if (contentWidth > 0) {
    x = getAxisDefaultTranslation(viewportWidth, contentWidth * scale, false, false);
  }
  if (contentHeight > 0) {
    y = getAxisDefaultTranslation(viewportHeight, contentHeight * scale, true, true);
  }
  return { x, y };
}

export type SpotGridPoint = { x: number; y: number };

export type ClampPanTranslationParams = {
  translateX: number;
  translateY: number;
  scale: number;
  contentWidth: number;
  contentHeight: number;
  viewportWidth: number;
  viewportHeight: number;
};

export type ComputePinchPanTranslationParams = {
  prevScale: number;
  nextScale: number;
  translateX: number;
  translateY: number;
  gestureFocalX: number;
  gestureFocalY: number;
};

/**
 * Keeps panned content inside the viewport so the zoomed grid cannot be dragged fully off-screen.
 * On each axis, pan is clamped to [0, viewportSize - scaledContentSize]: when content fits, that
 * range only allows a pan between 0 and the leftover space (it is not forced to stay centered);
 * when content overflows, pan is clamped to the edges as before.
 */
export function clampPanTranslation({
  translateX,
  translateY,
  scale,
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight
}: ClampPanTranslationParams): SpotGridPoint {
  'worklet';
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return { x: 0, y: 0 };
  }

  let x = translateX;
  let y = translateY;
  if (contentWidth > 0) {
    x = clampAxis(translateX, viewportWidth, contentWidth * scale);
  }
  if (contentHeight > 0) {
    y = clampAxis(translateY, viewportHeight, contentHeight * scale);
  }
  return { x, y };
}

/**
 * Whether pinch `onUpdate` should clamp pan on a single axis after a scale step.
 * - Zoom-in always clamps.
 * - Zoom-out clamps only while the axis still overflows the viewport. Once the scaled content
 *   fits the axis, clamping would snap the grid toward the origin/center as it crosses the
 *   fit↔overflow boundary, so it is deferred to pinch-end (AC1/AC2). While the axis still
 *   overflows, clamping keeps focal-anchored zoom-out from walking the pan off the content edge
 *   and snapping back on pinch-end.
 */
export function shouldClampAxisDuringPinchUpdate(
  prevScale: number,
  nextScale: number,
  viewportSize: number,
  contentSize: number
): boolean {
  'worklet';
  if (nextScale >= prevScale) {
    return true;
  }
  return contentSize * nextScale > viewportSize;
}

/** Content-space center of a spot cell (1-based column/row). */
export function getSpotCenterInContent(
  column: number,
  row: number,
  spotSize: number = SPOT_BASE_SIZE
): SpotGridPoint {
  const invalid =
    !Number.isFinite(column) ||
    !Number.isFinite(row) ||
    column < 1 ||
    row < 1 ||
    !Number.isInteger(column) ||
    !Number.isInteger(row);

  if (invalid) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.warn('getSpotCenterInContent: invalid spot coordinates', { column, row });
    }
    return {
      x: GRID_H_PADDING + spotSize / 2,
      y: GRID_PADDING_TOP + spotSize / 2
    };
  }

  const displayCol = column - 1;
  const displayRow = row - 1;
  return {
    x: GRID_H_PADDING + displayCol * (spotSize + GRID_GAP) + spotSize / 2,
    y: GRID_PADDING_TOP + displayRow * (spotSize + GRID_GAP) + spotSize / 2
  };
}

/**
 * Pan offset after a pinch scale step. Always anchors on the gesture focal point so zoom stays
 * under the user's fingers (AC2). Selected-spot overrides were removed — they caused the grid to
 * drift away from the pinch location when a spot was selected.
 */
export function computePinchPanTranslation({
  prevScale,
  nextScale,
  translateX,
  translateY,
  gestureFocalX,
  gestureFocalY
}: ComputePinchPanTranslationParams): SpotGridPoint {
  'worklet';
  if (prevScale <= 0 || nextScale === prevScale) {
    return { x: translateX, y: translateY };
  }

  const ratio = nextScale / prevScale;

  return {
    x: gestureFocalX - (gestureFocalX - translateX) * ratio,
    y: gestureFocalY - (gestureFocalY - translateY) * ratio
  };
}

/**
 * Fixed viewport height for pinch-zoom mode.
 * Uses the same cap at 1× and when zoomed so the layout does not jump on pinch and the legend
 * slot stays stable. Scaled content pans inside this viewport; in embed mode the parent page
 * scrolls when the full map (grid + legend) is taller than the slot.
 */
export type PanGestureActivation = 'activate' | 'fail' | 'wait';

/**
 * Manual pan activation: only claim the touch on an axis that can pan, and fail when the user
 * drags on a non-pannable axis (e.g. vertical page scroll over a wide 1× grid).
 */
export function resolvePanGestureActivation(
  dx: number,
  dy: number,
  canPanX: boolean,
  canPanY: boolean,
  activationOffsetPx: number
): PanGestureActivation {
  'worklet';
  if (!canPanX && !canPanY) {
    return 'fail';
  }

  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (!canPanY && canPanX) {
    if (absDy >= activationOffsetPx && absDy > absDx) return 'fail';
    if (absDx >= activationOffsetPx) return 'activate';
    return 'wait';
  }

  if (!canPanX && canPanY) {
    if (absDx >= activationOffsetPx && absDx > absDy) return 'fail';
    if (absDy >= activationOffsetPx) return 'activate';
    return 'wait';
  }

  if (absDx < activationOffsetPx && absDy < activationOffsetPx) {
    return 'wait';
  }

  if (absDx >= absDy) {
    return absDx >= activationOffsetPx ? 'activate' : 'fail';
  }

  return absDy >= activationOffsetPx ? 'activate' : 'fail';
}

export function getZoomViewportHeight(
  contentHeight: number,
  maxSlotHeight: number,
  embedInPageScroll = false
): number | undefined {
  if (contentHeight <= 0) return undefined;
  if (maxSlotHeight <= 0) {
    return embedInPageScroll ? contentHeight : undefined;
  }
  // In embed mode, let the grid keep its natural height when it exceeds the slot so the
  // parent ScrollView can scroll to rows below instead of clipping inside overflow:hidden.
  if (embedInPageScroll && contentHeight > maxSlotHeight) {
    return contentHeight;
  }
  return Math.min(contentHeight, maxSlotHeight);
}
